import { isRequestError } from 'utils/requestError';
import { buildCreateUserPayload } from 'utils/userPayload';
import { Button, InputWraper, useForm } from 'modules';
import React, { useCallback, useEffect } from 'react';
import {
  useCreateUserMutation,
  useGetCompanyAllQuery,
  useGetScreenActionPermissionsQuery,
} from 'services';
import toast from 'react-hot-toast';
import * as Yup from 'yup';
import {
  InputText,
  InputSelect,
  InputGroupedCheckbox,
  normalizeGroupedCheckboxValues,
} from 'components';

import { InputToggleSwitch } from '../../../components/toggle-switch';
import { PopupWraper } from '../../../components/popup';

import { CreateUserSuccess, CreateUserSuccessDefined } from './configs';
import { getUserRoleOptions } from '../list/configs';
import { Roles } from 'configs/roles';
import { ScreenActionPermissionItem } from 'services/openapi';
import { getUserRoles } from 'utils/helpers/auth';
import { BidvVaInput } from '../BidvVaInput';

interface CreateUserProps {
  onClose: () => void;
  forceUpdateTable: () => void;
  refetchLeadersList: () => void;
}

const CreateAccount = ({
  onClose,
  forceUpdateTable,
  refetchLeadersList,
}: CreateUserProps) => {
  const roles = getUserRoles();
  const [isUserActive, setIsUserActive] = React.useState<boolean>(true);
  const { mutateAsync, data: dataCreateUser } = useCreateUserMutation();
  const { data: companyInfo } = useGetCompanyAllQuery();

  const { data: dataScreenActionPermissions } =
    useGetScreenActionPermissionsQuery({
      enabled: roles.includes(Roles.SUPER_ADMIN),
    });

  const dataScreenActionPermissionsLst = useCallback(() => {
    if (!dataScreenActionPermissions) return [];
    return dataScreenActionPermissions?.data?.map(
      (item: ScreenActionPermissionItem) => ({
        label: item.value,
        value: item.key,
      }),
    );
  }, [dataScreenActionPermissions]);

  const { handleSubmit, getInputProps, resetForm, setValues } = useForm({
    structure: [
      {
        name: 'phoneNumber',
        label: 'Số điện thoại',
        validate: Yup.string()
          .required('Vui lòng nhập số điện thoại')
          .matches(
            /^0\d{9}$/,
            'Số điện thoại không hợp lệ, vui lòng nhập số điện thoại bắt đầu bằng số 0 và có 10 chữ số',
          ),
      },
      {
        name: 'fullName',
        label: 'Tên tài khoản',
        validate: Yup.string().required('Vui lòng nhập tên tài khoản'),
      },
      {
        name: 'citizenIdentityCardNo',
        label: 'CCCD/MST',
        validate: Yup.string()
          .required('Vui lòng nhập CCCD/MST')
          .matches(
            /^\d{6,}$/,
            'CCCD/MST không hợp lệ, vui lòng nhập tối thiểu 6 số',
          ),
      },
      {
        name: 'accountNumber',
        label: 'Tiểu khoản TPBank',
        validate: Yup.string(),
      },
      {
        name: 'bidvVaPayerId',
        label: 'Tiểu khoản BIDV',
        validate: Yup.string()
          .max(16, 'Tiểu khoản BIDV không được vượt quá 16 ký tự')
          .matches(/^[A-Z0-9]+$/, {
            message: 'Tiểu khoản BIDV phải viết hoa và chỉ chứa chữ cái và số',
            excludeEmptyString: true,
          }),
      },
      {
        name: 'vaName',
        label: 'Tên tài khoản VA BIDV',
        validate: Yup.string().max(
          200,
          'Tên tài khoản VA BIDV không được vượt quá 200 ký tự',
        ),
      },
      {
        name: 'email',
        label: 'Email',
        validate: Yup.string().email('Email không hợp lệ'),
      },
      {
        name: 'role',
        label: 'Vai trò',
        validate: Yup.string().required('Vui lòng chọn vai trò'),
      },
      {
        name: 'adminActions',
        label: 'Thao tác',
      },
      {
        name: 'compId',
        label: 'Doanh nghiệp/HTX',
        validate: Yup.string().when('role', {
          is: Roles.MC,
          then: Yup.string().required(
            'Vui lòng chọn Doanh nghiệp/HTX cho Đối tác',
          ),
          otherwise: Yup.string().nullable(),
        }),
      },
      // {
      //   name: 'agencyId',
      //   label: 'Phòng ban',
      //   // validate: Yup.string()
      //   //   .when('role', {
      //   //     is: 'Agency',
      //   //     then: Yup.string().required('Vui lòng chọn Phòng ban'),
      //   //     otherwise: Yup.string().nullable(),
      //   //   })
      //   // .test({
      //   //   test: function (value) {
      //   //     const role = this.resolve(Yup.ref('role'));
      //   //     // if (role === 'SpVisor' || role === 'Leader' || value) {
      //   //     if (role === 'SpVisor' || value) {
      //   //       return true;
      //   //     }
      //   //     return false;
      //   //   },
      //   //   message: 'Trường này chỉ được bỏ trống khi vai trò là Nhân viên đối soát hoặc Trưởng nhóm',
      //   // }),
      // },
    ],
    onSubmit: async (values) => {
      const normalizedPhoneNumber = values.phoneNumber.replace(/^0/, '+84');
      const normalizedValues = {
        ...values,
        phoneNumber: normalizedPhoneNumber,
        isActive: isUserActive,
        vaName: values.vaName || values.fullName,
      };
      if (normalizedValues.role !== Roles.ADMIN) {
        normalizedValues.adminActions = [];
      } else {
        normalizedValues.adminActions = normalizeGroupedCheckboxValues(
          normalizedValues.adminActions,
          dataScreenActionPermissionsLst(),
        );
      }
      try {
        await mutateAsync(buildCreateUserPayload(normalizedValues));
        resetForm();
        refetchLeadersList();
        forceUpdateTable();
        onClose();
      } catch (error: any) {
        if (!isRequestError(error)) {
          toast.error(error.message || 'Không thể tạo người dùng');
        }
      }
    },
  });

  const vaPrefix =
    companyInfo?.find((item) => item.compId === getInputProps('compId').value)
      ?.bidvVaPrefix || '';

  // const dataCompany = companyInfo?.map((item) => ({ label: `${item.compName}- ${item.compId}`, value: item.compId })) || [];
  const dataCompany =
    companyInfo?.map((item) => ({
      label: `${item.compName}`,
      value: item.compId,
    })) || [];

  const dataCompanyWithRoleAgency =
    companyInfo
      // ?.filter((item) => Object.keys(item.compUser).length !== 0)
      // ?.map((item) => ({ label: `${item.compName}- ${item.compId}`, value: item.compId })) || [];
      ?.map((item) => ({ label: `${item.compName}`, value: item.compId })) ||
    [];

  let checkedDataCompany: any = [];

  switch (getInputProps('role').value) {
    case 'Agency':
      checkedDataCompany = dataCompanyWithRoleAgency;
      break;

    // case 'Leader':
    //   checkedDataCompany = dataCompanyWithRoleLeader;
    //   break;

    default:
      checkedDataCompany = dataCompany;
      break;
  }

  const screenActionPermissionOptions = dataScreenActionPermissionsLst();

  useEffect(() => {
    if (dataCreateUser) {
      const { statusCode } = dataCreateUser;

      const dataDefined =
        CreateUserSuccessDefined[statusCode as CreateUserSuccess];

      if (statusCode === 201) {
        toast.success(dataDefined?.message, { duration: 1500 });
      }
    }
  }, [dataCreateUser]);
  return (
    <div className='wrap-pop-up-create-user'>
      <PopupWraper center title='Tạo mới tài khoản' onClose={() => onClose()}>
        <div className='container-fluid'>
          <div className='row wrap-form-create-user'>
            <InputWraper
              className='col-lg-6'
              inputProps={getInputProps('phoneNumber')}
              component={InputText}
            />
            <InputWraper
              className='col-lg-6'
              inputProps={getInputProps('fullName')}
              component={InputText}
            />
            <InputWraper
              className='col-lg-6'
              inputProps={getInputProps('citizenIdentityCardNo')}
              component={InputText}
            />
            <InputWraper
              className='col-lg-6'
              inputProps={getInputProps('accountNumber')}
              component={InputText}
            />
            <InputWraper
              className='col-lg-6 bidv-va-input'
              inputProps={getInputProps('bidvVaPayerId')}
              renderInput={(props) => (
                <BidvVaInput {...props} vaPrefix={vaPrefix} />
              )}
            />
            <InputWraper
              className='col-lg-6'
              inputProps={getInputProps('vaName')}
              component={InputText}
            />
            <InputWraper
              className='col-lg-6'
              inputProps={getInputProps('email')}
              component={InputText}
            />
            <InputWraper
              className='col-lg-6'
              inputProps={getInputProps('role')}
              renderInput={(props) => (
                <InputSelect
                  {...props}
                  onChange={(e) => {
                    if (e.value === Roles.ADMIN) {
                      setValues({ adminActions: [] });
                      setValues({ compId: '' });
                    }
                    props.onChange(e);
                  }}
                  options={getUserRoleOptions(roles)}
                />
              )}
            />
            {getInputProps('role').value === Roles.ADMIN && (
              <InputWraper
                className='col-12'
                inputProps={getInputProps('adminActions')}
                renderInput={(props) => (
                  <InputGroupedCheckbox
                    {...props}
                    options={screenActionPermissionOptions}
                    onResetAgency={() => setValues({ agencyId: '' })}
                  />
                )}
              />
            )}
            {getInputProps('role').value !== Roles.SUPER_ADMIN &&
              getInputProps('role').value !== Roles.ADMIN && (
                <InputWraper
                  className='col-lg-6'
                  inputProps={getInputProps('compId')}
                  renderInput={(props) => (
                    <InputSelect
                      {...props}
                      onChange={(e) => {
                        setValues({ agencyId: '' });
                        props.onChange(e);
                      }}
                      options={checkedDataCompany}
                    />
                  )}
                />
              )}

            {/* {getInputProps('compId').value && getInputProps('role').value !== 'SpVisor' && (
              // {getInputProps('compId').value && (
              <InputWraper
                className="col-lg-6"
                inputProps={getInputProps('agencyId')}
                renderInput={(props) => <InputSelect {...props} options={dataAgency} />}
              />
            )} */}
          </div>

          <div className='input'>
            <div className='label'>Trạng thái</div>
            <InputToggleSwitch
              onChange={async (value) => setIsUserActive(value)}
              value={isUserActive}
              onTouched={() => false}
              label=''
              name=''
            />
          </div>
        </div>

        <div className='wrap-button-create-user'>
          <Button
            label='Tạo mới'
            type='submit'
            onClick={handleSubmit}
            buttonType='success'
          />
          <Button label='Trở về' buttonType='grey' onClick={() => onClose()} />
        </div>
      </PopupWraper>
    </div>
  );
};

export default CreateAccount;
